import {Input} from '@/base/ui/input'
import {Label} from '@/base/ui/label'
import {cn} from '@/lib/utils'
import {cva} from 'class-variance-authority'
import React, {FormEvent, useEffect, useState} from 'react'
import {WuButton} from '../button'
import './WuStepper.css'

export interface IWuStepperProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onChange?: (e: number) => void
  readonly?: boolean
  resetInvalidValueOnBlur?: boolean
  invalid?: boolean
  label?: string
  defaultValue?: number
  dir?: 'ltr' | 'rtl'
  labelPosition?: 'left' | 'top'
}

export const WuStepper = React.forwardRef<HTMLInputElement, IWuStepperProps>(
  (props, ref) => {
    const {
      onChange,
      defaultValue = 0,
      min,
      max,
      step,
      readonly,
      resetInvalidValueOnBlur = true,
      value,
      label,
      labelPosition = 'top',
      className,
      style,
      ...rest
    } = props

    const [internalValue, setInternalValue] = useState(value || defaultValue)

    const disabled = React.useCallback(
      (x: 'inc' | 'dec'): boolean => {
        if (props.disabled || props.readOnly) return true
        if (x === 'inc' && Number(internalValue || 0) >= Number(max))
          return true
        if (x === 'dec' && Number(internalValue || 0) <= Number(min))
          return true
        return false
      },
      [min, max],
    )

    const inc = React.useCallback(
      (e: FormEvent) => {
        e.preventDefault()
        setInternalValue(p => Number(p || min || 0) + Number(step || 1))
      },
      [min, step],
    )
    const dec = React.useCallback(
      (e: FormEvent) => {
        e.preventDefault()
        setInternalValue(p => {
          if (!p) return 0
          return Number(p) - Number(step || 1)
        })
      },
      [min, step],
    )

    const isValid = React.useCallback(
      (v: number): boolean => v >= Number(min) && v <= Number(max),
      [min, max],
    )

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const v = e.currentTarget.value
      setInternalValue(v)
    }

    const handleOnBlur = React.useCallback((): void => {
      if (resetInvalidValueOnBlur && !isValid(Number(internalValue))) {
        const v = Number(min) || 0
        setInternalValue(v)
      }
    }, [min, resetInvalidValueOnBlur, isValid, internalValue])

    useEffect(() => {
      if (onChange) onChange(Number(internalValue))
    }, [internalValue, value])

    useEffect(() => {
      if (!!value && !!defaultValue) {
        console.warn(
          'As both value and defaultValue are passed, value will be used',
        )
      }
    }, [value, defaultValue])

    const customClass = cn(
      'px-10 h-[33px] text-center text-blue-q text-xs rounded-t-sm',
      'wu-stepper-input',
    )

    return (
      <div
        className={cn(
          labelPositionClasses({
            labelPosition,
          }),
          className,
          'wu-stepper',
        )}
        style={style}
        dir={rest.dir}
      >
        <Label
          htmlFor={rest.id}
          className="text-xs font-normal leading-6 text-gray-lead wu-stepper-label"
          dir={rest.dir}
        >
          {label}
        </Label>
        <div
          className="relative flex items-center justify-start w-fit wu-stepper-container"
          dir={rest.dir}
        >
          <WuButton
            variant="iconOnly"
            Icon={<span className="text-base wm-add"></span>}
            className={cn(
              'rounded-none bg-gray-10 absolute top-0 right-0 w-8 h-8 text-blue-q rounded-tr-sm',
              rest.dir === 'rtl' ? 'right-auto left-0' : '',
              'wu-stepper-button',
            )}
            onClick={inc}
            disabled={disabled('inc')}
            aria-label="increment"
          ></WuButton>

          <Input
            {...rest}
            ref={ref}
            type="number"
            className={customClass}
            onChange={handleOnChange}
            min={min}
            max={max}
            step={step}
            readOnly={readonly}
            onBlur={handleOnBlur}
            value={internalValue}
          />

          <WuButton
            variant="iconOnly"
            Icon={<span className="text-base wm-minimize"></span>}
            className={cn(
              'rounded-none bg-gray-10 absolute top-0 left-0 w-8 h-8 text-blue-q rounded-tl-sm',
              rest.dir === 'rtl' ? 'left-auto right-0' : '',
              'wu-stepper-button',
            )}
            onClick={dec}
            disabled={disabled('dec')}
            aria-label="decrement"
          ></WuButton>
        </div>
      </div>
    )
  },
)

const labelPositionClasses = cva('flex w-fit', {
  variants: {
    labelPosition: {
      left: 'flex-row text-right gap-2 items-center',
      top: 'flex-col',
    },
  },
})

WuStepper.displayName = 'WuStepper'
